import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';

import {
  ConfigurationInclusion,
  INCLUSION_CATEGORY_LABELS,
  INCLUSION_KIND_LABELS,
  InclusionCategory,
  InclusionKind,
} from '~/types/tours/tourOffer';

import NativeTourRemoveButton from './NativeTourRemoveButton';
import { duplicateTitles } from './nativeTourForm';

interface Props {
  value: Array<ConfigurationInclusion>;
  onChange: (value: Array<ConfigurationInclusion>) => void;
  emptyText: string;
}

const CATEGORIES = Object.keys(INCLUSION_CATEGORY_LABELS) as Array<InclusionCategory>;
const KINDS = Object.keys(INCLUSION_KIND_LABELS) as Array<InclusionKind>;

/** Inclusions with a title, an icon category and how they're called out. */
export default function NativeTourInclusionList({ value, onChange, emptyText }: Props) {
  const duplicates = duplicateTitles(value.map((inclusion) => inclusion.title)).map((title) => title.toLowerCase());
  const update = (id: string, change: Partial<ConfigurationInclusion>) =>
    onChange(value.map((inclusion) => (inclusion.id === id ? { ...inclusion, ...change } : inclusion)));

  return (
    <Stack spacing={1.5}>
      {value.length === 0 && <Typography color="text.secondary">{emptyText}</Typography>}
      {value.map((inclusion) => {
        const isDuplicate = duplicates.includes(inclusion.title.trim().toLowerCase());
        return (
          <Stack
            key={inclusion.id}
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ md: 'flex-start' }}
          >
            <TextField
              label="Inclusion"
              value={inclusion.title}
              onChange={(event) => update(inclusion.id, { title: event.target.value })}
              error={!inclusion.title.trim() || isDuplicate}
              helperText={!inclusion.title.trim() ? 'Required' : isDuplicate ? 'Already listed' : undefined}
              required
              fullWidth
            />
            <TextField
              select
              label="Icon"
              value={inclusion.category}
              onChange={(event) => update(inclusion.id, { category: event.target.value as InclusionCategory })}
              sx={{ minWidth: 220 }}
            >
              {CATEGORIES.map((category) => (
                <MenuItem key={category} value={category}>
                  {INCLUSION_CATEGORY_LABELS[category]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Shown as"
              value={inclusion.kind}
              onChange={(event) => update(inclusion.id, { kind: event.target.value as InclusionKind })}
              sx={{ minWidth: 160 }}
            >
              {KINDS.map((kind) => (
                <MenuItem key={kind} value={kind}>
                  {INCLUSION_KIND_LABELS[kind]}
                </MenuItem>
              ))}
            </TextField>
            <NativeTourRemoveButton
              label="Remove inclusion"
              onClick={() => onChange(value.filter((other) => other.id !== inclusion.id))}
            />
          </Stack>
        );
      })}
      <Box>
        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            onChange([
              ...value,
              { type: 'inclusion', id: crypto.randomUUID(), title: '', category: 'other', kind: 'standard' },
            ])
          }
        >
          Add inclusion
        </Button>
      </Box>
    </Stack>
  );
}
